import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Label,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  Progress,
  ProgressMeasureLocation,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CheckCircleIcon, ExclamationCircleIcon, SearchIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { chargeInvoiceAutopay, useAdminInvoiceAutopay } from '../../../hooks/useAdminInvoiceAutopay';
import type {
  AdminInvoiceAutopayInvoice,
  AdminInvoiceAutopayPreview,
  AdminInvoiceAutopaySummary,
  AutopayOutcome,
} from '../../../types/models/Wallet';
import { formatDate, formatDateTime } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';
import { formatCardExpiry } from '../../Organization/Billing/paymentDisplay';
import {
  AUTOPAY_OUTCOME_LABEL,
  AUTOPAY_OUTCOME_VARIANT,
  AUTOPAY_PLAN_LABEL,
  NOTICE_LABEL_COLOR,
  NOTICE_SR_TITLE,
  cardText,
  isPayablePlan,
} from '../../Organization/Billing/invoiceAutopayDisplay';

const invoiceRef = (row: Pick<AdminInvoiceAutopayInvoice, 'numero' | 'invoice_id'>): string =>
  row.numero ?? `id ${row.invoice_id}`;

/** One invoice of a finished "Charge all" run. */
interface RunResult {
  invoiceId: number;
  numero: string | null;
  outcome: AutopayOutcome;
  message: string;
}

type RunState =
  | { phase: 'confirm'; queue: AdminInvoiceAutopayInvoice[]; summary: AdminInvoiceAutopaySummary }
  | { phase: 'running' | 'done'; queue: AdminInvoiceAutopayInvoice[]; results: RunResult[]; stopError: string | null };

const summaryText = (summary: AdminInvoiceAutopaySummary): string =>
  `${summary.chargeable} payable: ${formatPrice(summary.total)} — ${formatPrice(summary.from_wallet)} from the balance, ` +
  `${formatPrice(summary.by_card)} by card. Balance after: ${formatPrice(summary.wallet_after)}.`;

/** How a row would be charged, for its confirm modal. */
const chargeWay = (row: AdminInvoiceAutopayInvoice, preview: AdminInvoiceAutopayPreview): string =>
  row.plan === 'wallet' ? 'from the prepaid balance' : `to ${preview.card ? cardText(preview.card) : 'the saved card'}`;

const ConsentCard: React.FunctionComponent<{ preview: AdminInvoiceAutopayPreview }> = ({ preview }) => {
  const consent = preview.invoice_autopay;
  const changedBy = consent.changed_by;
  return (
    <Card className="pf-v6-u-mb-md">
      <CardTitle>The organization&apos;s consent</CardTitle>
      <CardBody>
        <DescriptionList isHorizontal isCompact>
          <DescriptionListGroup>
            <DescriptionListTerm>Automatic payment</DescriptionListTerm>
            <DescriptionListDescription>
              {consent.enabled ? (
                <Label isCompact color="green">
                  On
                </Label>
              ) : (
                <Label isCompact color="grey">
                  Off
                </Label>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Accepted terms</DescriptionListTerm>
            <DescriptionListDescription>
              {consent.accepted_terms_version ?? '—'}
              {consent.enabled && !consent.terms_current && (
                <Label isCompact color="orange" className="pf-v6-u-ml-sm">
                  Older terms
                </Label>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Last changed</DescriptionListTerm>
            <DescriptionListDescription>
              {formatDateTime(consent.changed_at)}
              {changedBy && ` by ${changedBy.name ?? changedBy.email ?? `#${changedBy.id}`}`}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Prepaid balance</DescriptionListTerm>
            <DescriptionListDescription>{formatPrice(preview.wallet.balance)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Saved card</DescriptionListTerm>
            <DescriptionListDescription>
              {preview.card ? `${cardText(preview.card)}, expires ${formatCardExpiry(preview.card)}` : 'None'}
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </CardBody>
    </Card>
  );
};

interface UnpaidInvoicesTabProps {
  clientId: number;
  /** The tab is the active one — the preview (a live Facturier read) is fetched only then. */
  isActive: boolean;
  /** A charge ran: the parent refetches the wallet detail (balance, ledger, invoice payments). */
  onCharged: () => void;
}

/**
 * Admin › Wallets › {organization} › "Unpaid invoices" (docs/api-types/admin-invoice-autopay.md): every
 * unpaid invoice with what a charge would do today, and staff charges on the organization's OWN consent —
 * its opt-in to the automatic payment of all its invoices. The staff user is recorded as the person who
 * charged each one. "Charge all payable" sends one request per payable invoice, one after the other, in
 * the preview's order (oldest first): the preview simulates the balance in that order, so another order
 * could change which invoice the balance pays.
 */
const UnpaidInvoicesTab: React.FunctionComponent<UnpaidInvoicesTabProps> = ({ clientId, isActive, onCharged }) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { preview, available, loading, error, refetch } = useAdminInvoiceAutopay(clientId, isActive);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [confirmRow, setConfirmRow] = useState<AdminInvoiceAutopayInvoice | null>(null);
  const [isCharging, setIsCharging] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [run, setRun] = useState<RunState | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const invoices = useMemo(() => preview?.invoices ?? [], [preview]);
  // Filtering keeps the preview's order — never re-sorted.
  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (q === '') {
      return invoices;
    }
    return invoices.filter(
      (r) => (r.numero ?? '').toLowerCase().includes(q) || (r.intitule ?? '').toLowerCase().includes(q),
    );
  }, [invoices, debouncedSearch]);
  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setPage(1);
  };

  const refreshAll = () => {
    void refetch();
    onCharged();
  };

  const chargeOne = async (row: AdminInvoiceAutopayInvoice) => {
    setIsCharging(true);
    setRowError(null);
    const response = await chargeInvoiceAutopay(apiFetch, clientId, row.invoice_id);
    setIsCharging(false);
    if (response.kind === 'outcome') {
      const { outcome, message } = response.result;
      const variant = AUTOPAY_OUTCOME_VARIANT[outcome];
      addNotification({
        title: `Invoice #${invoiceRef(row)}: ${AUTOPAY_OUTCOME_LABEL[outcome]}`,
        srTitle: NOTICE_SR_TITLE[variant],
        description: message,
        variant,
      });
      setDrawerOpen(true);
      setConfirmRow(null);
      refreshAll();
      return;
    }
    // Kept in the modal too: its backdrop covers the notification drawer.
    const message =
      response.kind === 'error' ? response.message : 'The staff charge is not available on this backend yet.';
    setRowError(message);
    addNotification({
      title: `Could not charge invoice #${invoiceRef(row)}`,
      srTitle: NOTICE_SR_TITLE.danger,
      description: message,
      variant: 'danger',
    });
    setDrawerOpen(true);
    refreshAll();
  };

  const openChargeAll = () => {
    if (!preview) {
      return;
    }
    // The run charges exactly what was confirmed: the payable rows as displayed, in the preview's order.
    setRun({
      phase: 'confirm',
      queue: preview.invoices.filter((r) => isPayablePlan(r.plan)),
      summary: preview.summary,
    });
  };

  const chargeAll = async (queue: AdminInvoiceAutopayInvoice[]) => {
    const results: RunResult[] = [];
    let stopError: string | null = null;
    setRun({ phase: 'running', queue, results: [], stopError: null });
    // Strictly one at a time, never in parallel: each charge moves the balance the next one sees.
    for (const row of queue) {
      const response = await chargeInvoiceAutopay(apiFetch, clientId, row.invoice_id);
      if (response.kind !== 'outcome') {
        stopError =
          response.kind === 'error' ? response.message : 'The staff charge is not available on this backend yet.';
        break;
      }
      results.push({
        invoiceId: row.invoice_id,
        numero: row.numero,
        outcome: response.result.outcome,
        message: response.result.message,
      });
      setRun({ phase: 'running', queue, results: [...results], stopError: null });
    }
    setRun({ phase: 'done', queue, results, stopError });

    const paid = results.filter((r) => r.outcome === 'paid').length;
    const inFlight = results.filter((r) => r.outcome === 'in_flight').length;
    const other = results.length - paid - inFlight;
    const variant = stopError !== null ? 'danger' : other > 0 ? 'warning' : 'success';
    addNotification({
      title: `${paid} paid, ${inFlight} in progress, ${other} not charged`,
      srTitle: NOTICE_SR_TITLE[variant],
      description:
        stopError !== null
          ? `Stopped after ${results.length} of ${queue.length} invoices: ${stopError}`
          : `${queue.length} invoices processed, oldest first.`,
      variant,
    });
    setDrawerOpen(true);
    refreshAll();
  };

  if (loading && preview === null) {
    return <CenteredSpinner />;
  }
  if (!available) {
    return (
      <EmptyState titleText="Not available yet" icon={ExclamationCircleIcon} headingLevel="h3" variant="sm">
        <EmptyStateBody>
          The backend does not serve the staff charge of unpaid invoices yet (this route answers 404).
        </EmptyStateBody>
      </EmptyState>
    );
  }
  if (error || preview === null) {
    return (
      <EmptyState
        titleText="Unable to load the unpaid invoices"
        icon={ExclamationCircleIcon}
        status="danger"
        headingLevel="h3"
        variant="sm"
      >
        <EmptyStateBody>{error ?? 'Unknown error'}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  const enabled = preview.invoice_autopay.enabled;
  const busy = isCharging || (run !== null && run.phase === 'running');

  const pagination = (variant?: 'bottom') => (
    <Pagination
      itemCount={filtered.length}
      perPage={perPage}
      page={page}
      onSetPage={(_e, p) => setPage(p)}
      onPerPageSelect={(_e, pp) => {
        setPerPage(pp);
        setPage(1);
      }}
      variant={variant}
    />
  );

  const renderTable = () => {
    if (invoices.length === 0) {
      return (
        <EmptyState titleText="No unpaid invoice" icon={CheckCircleIcon} headingLevel="h3" variant="sm">
          <EmptyStateBody>Facturier lists no unpaid invoice for this organization.</EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by number or title..."
                value={searchValue}
                onChange={(_e, value) => setSearchValue(value)}
                onClear={clearSearch}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">{pagination()}</ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {filtered.length === 0 ? (
          <EmptyState titleText="No invoice matches" icon={SearchIcon} headingLevel="h3" variant="sm">
            <EmptyStateBody>No unpaid invoice matches &quot;{debouncedSearch}&quot;.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="link" onClick={clearSearch}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <>
            <Table aria-label="Unpaid invoices of this organization" variant="compact">
              <Thead>
                <Tr>
                  <Th>Number</Th>
                  <Th>Date</Th>
                  <Th>Title</Th>
                  <Th className="pf-v6-u-text-align-end">Amount</Th>
                  <Th>Plan</Th>
                  <Th screenReaderText="Actions" />
                </Tr>
              </Thead>
              <Tbody>
                {paginated.map((row) => {
                  const plan = AUTOPAY_PLAN_LABEL[row.plan];
                  const shownAmount = row.amount ?? row.tvac;
                  return (
                    <Tr key={row.invoice_id}>
                      <Td dataLabel="Number">{row.numero ?? '—'}</Td>
                      <Td dataLabel="Date">{formatDate(row.date)}</Td>
                      <Td dataLabel="Title">{row.intitule ?? '—'}</Td>
                      <Td dataLabel="Amount" className="pf-v6-u-text-align-end">
                        {shownAmount !== null ? formatPrice(shownAmount) : '—'}
                      </Td>
                      <Td dataLabel="Plan">
                        {row.plan === 'refused' && row.refusal ? (
                          <Tooltip content={row.refusal.message}>
                            <Label isCompact color={plan.color} tabIndex={0}>
                              {plan.text}
                            </Label>
                          </Tooltip>
                        ) : (
                          <Label isCompact color={plan.color}>
                            {plan.text}
                          </Label>
                        )}
                      </Td>
                      <Td dataLabel="Actions" isActionCell>
                        {enabled && isPayablePlan(row.plan) && (
                          <Button
                            variant="link"
                            isInline
                            onClick={() => {
                              setRowError(null);
                              setConfirmRow(row);
                            }}
                            isDisabled={busy}
                            aria-label={`Charge invoice ${invoiceRef(row)}`}
                          >
                            Charge
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
            {pagination('bottom')}
          </>
        )}
      </>
    );
  };

  const renderRunBody = () => {
    if (run === null) {
      return null;
    }
    if (run.phase === 'confirm') {
      return (
        <>
          <Content component="p">{summaryText(run.summary)}</Content>
          <Content component="p">
            Each invoice is charged separately, oldest first. Keep this page open until it finishes.
          </Content>
        </>
      );
    }
    const total = run.queue.length;
    return (
      <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
        <FlexItem>
          <Progress
            title={run.phase === 'running' ? 'Charging invoices' : 'Charged invoices'}
            value={run.results.length}
            min={0}
            max={Math.max(total, 1)}
            measureLocation={ProgressMeasureLocation.top}
            label={`${run.results.length} / ${total}`}
            valueText={`${run.results.length} of ${total} invoices`}
            variant={run.stopError !== null ? 'danger' : undefined}
          />
        </FlexItem>
        {run.stopError !== null && (
          <FlexItem>
            <Alert variant="danger" isInline title="The run stopped">
              {run.stopError}
            </Alert>
          </FlexItem>
        )}
        {run.results.length > 0 && (
          <FlexItem>
            <List isPlain aria-live="polite">
              {run.results.map((r) => {
                const variant = AUTOPAY_OUTCOME_VARIANT[r.outcome];
                return (
                  <ListItem key={r.invoiceId}>
                    <strong>#{invoiceRef({ numero: r.numero, invoice_id: r.invoiceId })}</strong>{' '}
                    <Label isCompact color={NOTICE_LABEL_COLOR[variant]}>
                      {AUTOPAY_OUTCOME_LABEL[r.outcome]}
                    </Label>{' '}
                    {r.message}
                  </ListItem>
                );
              })}
            </List>
          </FlexItem>
        )}
      </Flex>
    );
  };

  return (
    <>
      <ConsentCard preview={preview} />
      {!enabled && (
        <Alert
          variant="info"
          isInline
          title="Automatic payment of invoices is off — nothing can be charged"
          className="pf-v6-u-mb-md"
        />
      )}
      {preview.truncated && (
        <Alert
          variant="warning"
          isInline
          title="Only the 500 most recent unpaid invoices are shown."
          className="pf-v6-u-mb-md"
        />
      )}
      <Flex
        justifyContent={{ default: 'justifyContentSpaceBetween' }}
        alignItems={{ default: 'alignItemsCenter' }}
        className="pf-v6-u-mb-md"
      >
        <FlexItem>
          {enabled && (
            <Content component="p">
              {preview.summary.chargeable > 0 ? summaryText(preview.summary) : 'No invoice can be charged now.'}
            </Content>
          )}
        </FlexItem>
        <FlexItem>
          <Flex spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading || busy}
              >
                Refresh
              </Button>
            </FlexItem>
            {enabled && (
              <FlexItem>
                <Button variant="primary" onClick={openChargeAll} isDisabled={preview.summary.chargeable === 0 || busy}>
                  Charge all payable ({preview.summary.chargeable})
                </Button>
              </FlexItem>
            )}
          </Flex>
        </FlexItem>
      </Flex>
      {renderTable()}

      {confirmRow !== null && (
        <Modal
          variant="small"
          isOpen
          onClose={isCharging ? undefined : () => setConfirmRow(null)}
          aria-labelledby="charge-invoice-title"
        >
          <ModalHeader
            title={`Charge invoice #${invoiceRef(confirmRow)}?`}
            titleIconVariant="warning"
            labelId="charge-invoice-title"
          />
          <ModalBody>
            <Content component="p">
              Charge {formatPrice(confirmRow.amount ?? '0')} {chargeWay(confirmRow, preview)} on the organization&apos;s
              own consent to automatic payment. You are recorded as the person who charged it.
            </Content>
            {rowError && <Alert variant="danger" isInline title={rowError} className="pf-v6-u-mt-md" />}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="danger"
              onClick={() => void chargeOne(confirmRow)}
              isLoading={isCharging}
              isDisabled={isCharging}
            >
              Charge {formatPrice(confirmRow.amount ?? '0')}
            </Button>
            <Button variant="link" onClick={() => setConfirmRow(null)} isDisabled={isCharging}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {run !== null && (
        <Modal
          variant="medium"
          isOpen
          onClose={run.phase === 'running' ? undefined : () => setRun(null)}
          aria-labelledby="charge-all-title"
        >
          <ModalHeader
            title={run.phase === 'confirm' ? `Charge ${run.queue.length} invoices?` : 'Charge all payable'}
            titleIconVariant={run.phase === 'confirm' ? 'warning' : undefined}
            labelId="charge-all-title"
          />
          <ModalBody>{renderRunBody()}</ModalBody>
          <ModalFooter>
            {run.phase === 'confirm' && (
              <>
                <Button variant="danger" onClick={() => void chargeAll(run.queue)}>
                  Charge {run.queue.length} invoices
                </Button>
                <Button variant="link" onClick={() => setRun(null)}>
                  Cancel
                </Button>
              </>
            )}
            {run.phase === 'running' && (
              <Button variant="primary" isLoading isDisabled>
                Charging…
              </Button>
            )}
            {run.phase === 'done' && (
              <Button variant="primary" onClick={() => setRun(null)}>
                Close
              </Button>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default UnpaidInvoicesTab;
